import Head from 'next/head';
import Link from 'next/link';
import { useState } from 'react';

import BreadCrumbs from '../../components/breadcrumbs';
import List, { ListHeaderItem, ListItem } from '../../components/list';
import CreateOrganisation from '../../components/modals/create-organisation';
import Pagination from '../../components/pagination';
import Sidenav from '../../components/sidenav';
import { Spacer } from '../../components/spacer';
import { PageLoader } from '../../components/spinner';
import { Button } from '../../components/styled/button';
import { InputField, SelectField } from '../../components/styled/input';
import {
  Content,
  ContentHeader,
  HideM,
  Layout,
  Main,
  SidenavLayout,
} from '../../components/styled/layout';
import { Heading, Text } from '../../components/styled/text';
import { withAuth } from '../../components/with-auth';
import { usePageInput } from '../../hooks/use-page-input';
import { superuser } from '../../lib/config/roles-and-permissions';
import {
  useOrganisationFiltersQuery,
  useOrganisationListQuery,
} from '../../lib/graphql';
import dateFormat from '../../lib/util/date-format';

const breadcrumbs = [
  {
    name: 'Alle organisaties',
    path: '/organisations',
  },
];

function Organisations() {
  const [showCreateOrganisation, setShowCreateOrganisation] = useState(false);
  const [filter, setFilter] = useState({
    membership: null,
  });
  const { pageInput, handlePagination, handleQuery, resetOffset } =
    usePageInput();
  const [{ fetching, data, error }] = useOrganisationListQuery({
    variables: {
      input: pageInput,
      filter,
    },
  });
  const [{ data: filters }] = useOrganisationFiltersQuery();

  function handleFilter(e) {
    const { value, name } = e.target;
    if (value === '') {
      setFilter({
        ...filter,
        [name]: null,
      });
    } else {
      setFilter({
        ...filter,
        [name]: value,
      });
    }
    resetOffset();
  }

  return (
    <>
      <Head>
        <title>Organisaties – CorporatieNL</title>
      </Head>
      <Main>
        <SidenavLayout>
          <Sidenav />
          <div>
            <ContentHeader>
              <div>
                <Heading size={1}>Organisaties</Heading>
                <Spacer size={0.125} />
                <BreadCrumbs breadcrumbs={breadcrumbs} />
              </div>
              <Button onClick={() => setShowCreateOrganisation(true)}>
                <span className="material-icons">add</span>
                <Spacer size={0.25} /> <HideM>Organisatie&nbsp;</HideM>
                toevoegen
              </Button>
            </ContentHeader>
            <Content>
              <Layout columns="5fr auto 1fr">
                <InputField
                  value={pageInput.query}
                  onChange={handleQuery}
                  icon="search"
                  placeholder="Zoek op organisatie..."
                />
                <Spacer size={1} />
                <SelectField
                  defaultValue=""
                  onChange={handleFilter}
                  name="membership"
                  options={
                    <>
                      <option value="">Alle lidmaatschappen</option>
                      {filters?.memberships?.map((membership) => (
                        <option value={membership.id} key={membership.id}>
                          {membership.name}
                        </option>
                      ))}
                    </>
                  }
                ></SelectField>
              </Layout>
              <Spacer size={1} />
              {fetching ? <PageLoader /> : null}
              {!fetching && data && data.organisations ? (
                <>
                  <List
                    columnsMobile="1fr 1.5rem"
                    columns="4fr 2fr 2fr 2fr 1.5rem"
                    listHeaderItems={
                      <>
                        <ListHeaderItem>Organisatie</ListHeaderItem>
                        <HideM>
                          <ListHeaderItem>Lid sinds</ListHeaderItem>
                        </HideM>
                        <HideM>
                          <ListHeaderItem>Contactpersoon</ListHeaderItem>
                        </HideM>
                        <HideM>
                          <ListHeaderItem>Lidmaatschap</ListHeaderItem>
                        </HideM>
                      </>
                    }
                    listItems={data.organisations?.edges?.map(
                      (organisation) => (
                        <Link
                          key={organisation.id}
                          href={`/organisations/${organisation.id}`}
                        >
                          <a>
                            <ListItem
                              hasAction
                              columnsMobile="1fr 1.5rem"
                              columns="4fr 2fr 2fr 2fr 1.5rem"
                            >
                              <Text strong>{organisation.name}</Text>
                              <HideM>
                                <Text>
                                  {dateFormat(organisation.createdAt)}
                                </Text>
                              </HideM>
                              <HideM>
                                <Text>
                                  {organisation.contactDetails?.name}{' '}
                                  {organisation.contactDetails?.surname}
                                </Text>
                              </HideM>
                              <HideM>
                                <Text>{organisation?.membership?.name}</Text>
                              </HideM>
                              <span className="material-icons">
                                chevron_right
                              </span>
                            </ListItem>
                          </a>
                        </Link>
                      )
                    )}
                  />
                  <Spacer size={1.5} />
                  <Pagination
                    metadata={data?.organisations?.metadata}
                    onNext={handlePagination}
                    onPrevious={handlePagination}
                    onPageClick={handlePagination}
                  />
                </>
              ) : null}
            </Content>
          </div>
        </SidenavLayout>
      </Main>

      {showCreateOrganisation && (
        <CreateOrganisation close={() => setShowCreateOrganisation(false)} />
      )}
    </>
  );
}

export default withAuth(Organisations, superuser, {
  redirectTo: '/knowledge-base',
});
